/**
 * ISO weeks ("2026-W40"), the unit of the weekly activity report (CRA).
 *
 * Dates are handled as UTC midnights so that week arithmetic never crosses a DST change; "today"
 * is the calendar date in Europe/Paris, the timezone the backend computes weeks and deadlines in.
 */
import { currentLocale, t } from "./i18n";

export type IsoWeek = string;

const ISO_WEEK = /^(\d{4})-W(\d{2})$/;
const DAY = 86_400_000;
export const APP_TIMEZONE = "Europe/Paris";

/** UTC midnight of the calendar date `now` falls on in Europe/Paris. */
export function todayInParis(now: Date = new Date()): Date {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return new Date(Date.UTC(get("year"), get("month") - 1, get("day")));
}

/** "2026-10-06" for a UTC-midnight date. */
export function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Today's date in Europe/Paris, as "AAAA-MM-JJ" (the value of an `<input type="date">`). */
export function todayIso(now: Date = new Date()): string {
  return isoDate(todayInParis(now));
}

/** The ISO week a (UTC-midnight) date belongs to. */
export function weekOfDate(date: Date): IsoWeek {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const weekday = d.getUTCDay() || 7; // 1 = Monday … 7 = Sunday
  d.setUTCDate(d.getUTCDate() + 4 - weekday); // the Thursday decides the year
  const year = d.getUTCFullYear();
  const week = Math.ceil(((d.getTime() - Date.UTC(year, 0, 1)) / DAY + 1) / 7);
  return `${year}-W${String(week).padStart(2, "0")}`;
}

function mondayOfParts(year: number, week: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4)); // always in week 1
  const weekday = jan4.getUTCDay() || 7;
  return new Date(jan4.getTime() + ((week - 1) * 7 - (weekday - 1)) * DAY);
}

export function isIsoWeek(value: string | null | undefined): value is IsoWeek {
  if (!value) return false;
  const match = ISO_WEEK.exec(value.trim());
  if (!match) return false;
  const week = Number(match[2]);
  if (week < 1 || week > 53) return false;
  // Week 53 only exists in long years: the round trip tells.
  return weekOfDate(mondayOfParts(Number(match[1]), week)) === value.trim();
}

/** Monday (UTC midnight) of an ISO week. Throws on a malformed week. */
export function mondayOf(week: IsoWeek): Date {
  const match = ISO_WEEK.exec(week.trim());
  if (!match || !isIsoWeek(week)) throw new Error(`semaine illisible : ${week}`);
  return mondayOfParts(Number(match[1]), Number(match[2]));
}

export function sundayOf(week: IsoWeek): Date {
  return new Date(mondayOf(week).getTime() + 6 * DAY);
}

export function shiftWeek(week: IsoWeek, delta: number): IsoWeek {
  return weekOfDate(new Date(mondayOf(week).getTime() + delta * 7 * DAY));
}

export function currentWeek(now: Date = new Date()): IsoWeek {
  return weekOfDate(todayInParis(now));
}

/** The week reported by default: the one before the current week. */
export function previousWeek(now: Date = new Date()): IsoWeek {
  return shiftWeek(currentWeek(now), -1);
}

/** Lexicographic order is chronological thanks to the zero padding. */
export function compareWeeks(a: IsoWeek, b: IsoWeek): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** `count` weeks ending at `last` (included), most recent first. */
export function recentWeeks(count: number, last: IsoWeek = currentWeek()): IsoWeek[] {
  return Array.from({ length: count }, (_, i) => shiftWeek(last, -i));
}

export function weekNumber(week: IsoWeek): number {
  return Number(week.slice(-2));
}

/** "28 sept. – 4 oct. 2026" (fr) / "28 Sept – 4 Oct 2026" (en) */
export function weekSpan(week: IsoWeek): string {
  const locale = currentLocale();
  const dayMonth = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" });
  const dayMonthYear = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  return `${dayMonth.format(mondayOf(week))} – ${dayMonthYear.format(sundayOf(week))}`;
}

/** "Semaine 40 · 28 sept. – 4 oct. 2026" / "Week 40 · …" */
export function weekLabel(week: IsoWeek): string {
  return t("Week {{number}} · {{span}}", { number: weekNumber(week), span: weekSpan(week) });
}

/** "S40 · 28 sept. – 4 oct. 2026" / "W40 · …", for compact lists and selects. */
export function weekShortLabel(week: IsoWeek): string {
  return t("W{{number}} · {{span}}", { number: weekNumber(week), span: weekSpan(week) });
}
